import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Subscription, combineLatest, of } from 'rxjs';

import { ReportQuery, ReportQueryService } from '../../../services/report-query';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';
import {
  DashboardTimeWindowOption,
  dashboardErrorMessage,
  dashboardLayoutStyle,
  dashboardWindowQuery,
  loadDashboardScalar,
} from '../../widget-and-graphs.models';

export type DashboardPercentagePresentation = 'bar' | 'radial';

@Component({
  selector: 'app-dashboard-percentage-widget',
  standalone: false,
  templateUrl: './dashboard-percentage-widget.html',
  styleUrl: './dashboard-percentage-widget.scss',
})
export class DashboardPercentageWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input({ required: true }) title = '';
  @Input({ required: true }) query!: ReportQuery;
  @Input() changeQuery?: ReportQuery;
  @Input() caption = '';
  @Input() presentation: DashboardPercentagePresentation = 'bar';
  @Input() color = '#206bc4';
  @Input() showTimeWindowPicker = true;
  @Input() fixedTimeWindowLabel = '';
  @Input() timeWindowOptions: DashboardTimeWindowOption[] = [
    { label: 'Last 7 days', duration: 7 },
    { label: 'Last 30 days', duration: 30 },
    { label: 'Last 3 months', duration: 90 },
  ];
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 3 } };

  loading = false;
  error = '';
  value = 0;
  change = 0;
  rangeOpen = false;
  selectedWindowIndex = 0;
  private request?: Subscription;

  constructor(
    private readonly reportQuery: ReportQueryService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('attr.style')
  get layoutStyles(): string {
    return dashboardLayoutStyle(this.layout, this.color);
  }

  get boundedValue(): number {
    return Math.max(0, Math.min(100, this.value));
  }

  get selectedWindow(): DashboardTimeWindowOption {
    return this.timeWindowOptions[this.selectedWindowIndex]
      ?? { label: this.fixedTimeWindowLabel || 'Selected period', duration: this.query?.duration ?? 30 };
  }

  get displayedWindowLabel(): string {
    return this.showTimeWindowPicker ? this.selectedWindow.label : this.fixedTimeWindowLabel;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query'] || changes['changeQuery'] || changes['timeWindowOptions']) {
      const match = this.timeWindowOptions.findIndex((option) => option.duration === this.query?.duration);
      this.selectedWindowIndex = match >= 0 ? match : 0;
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  selectWindow(index: number): void {
    this.selectedWindowIndex = index;
    this.rangeOpen = false;
    this.load();
  }

  private load(): void {
    this.request?.unsubscribe();
    this.error = '';
    if (!this.query) return;
    const option = this.selectedWindow;
    const valueQuery = this.showTimeWindowPicker
      ? dashboardWindowQuery(this.reportQuery, this.query, option)
      : this.query;
    const changeQuery = this.changeQuery
      ? (this.showTimeWindowPicker
        ? dashboardWindowQuery(this.reportQuery, this.changeQuery, option)
        : this.changeQuery)
      : undefined;
    this.loading = true;
    this.request = combineLatest([
      loadDashboardScalar(this.reportQuery, valueQuery, 'percentage'),
      changeQuery ? loadDashboardScalar(this.reportQuery, changeQuery, 'percentage') : of(undefined),
    ]).subscribe({
      next: ([value, change]) => {
        this.value = value.value;
        this.change = change?.value ?? 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.error = dashboardErrorMessage(error);
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }
}
